import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageSquareQuote, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { Reflection } from '@/api/types';
import { cn } from '@/lib/cn';
import { ago } from '@/lib/format';

export function Reflections() {
  const [agent, setAgent] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [debounced, setDebounced] = useState<string>('');

  // Debounce search to avoid hammering the backend on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data: agents } = useQuery({
    queryKey: ['reflectionAgents'],
    queryFn: dashboardApi.reflectionAgents,
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['reflections', agent, debounced],
    queryFn: () => dashboardApi.reflections({
      agent: agent || undefined,
      q: debounced || undefined,
    }),
    refetchInterval: 5 * 60_000,
  });

  const rows = useMemo(() => data ?? [], [data]);

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <MessageSquareQuote size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Reflection Library</h1>
          <div className="text-xs text-text-faint">
            {isLoading ? 'loading…'
              : isError ? 'unable to reach /api/reflections'
              : `${rows.length} reflections · last 60 days · refreshed ${ago(new Date(dataUpdatedAt).toISOString())}`}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 items-center max-w-3xl">
        <select
          value={agent}
          onChange={(e) => setAgent(e.target.value)}
          className="bg-surface-2 border border-[color:var(--border)] rounded-md px-2 py-1 text-xs text-text"
        >
          <option value="">All agents</option>
          {(agents ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>

        <div className="flex-1 relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-faint pointer-events-none" />
          <input
            type="search"
            placeholder="Search reflection text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-surface-2 border border-[color:var(--border)] rounded-md pl-7 pr-2 py-1 text-xs text-text placeholder:text-text-faint"
          />
        </div>
      </div>

      {/* Reflections */}
      {rows.length === 0 && !isLoading
        ? <div className="card card-tight text-xs text-text-faint italic max-w-3xl">
            {debounced
              ? `No reflections match "${debounced}". Try a broader search or clear the agent filter.`
              : 'No reflections in the lookback window. They show up after each EOD wrap.'}
          </div>
        : <div className="space-y-3 max-w-3xl">
            {rows.map((r) => <ReflectionCard key={r.sessionId} r={r} highlight={debounced} />)}
          </div>}
    </div>
  );
}

const PREVIEW_CHARS = 280;

function ReflectionCard({ r, highlight }: { r: Reflection; highlight: string }) {
  const [expanded, setExpanded] = useState(false);
  const needsCollapse = r.reflection.length > PREVIEW_CHARS;
  const body = expanded || !needsCollapse
    ? r.reflection
    : r.reflection.slice(0, PREVIEW_CHARS).trimEnd() + '…';

  return (
    <article className="card card-tight">
      <header className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="text-sm font-semibold font-mono text-kam-violet">{r.agentName}</span>
        <span className="text-xs text-text-faint">·</span>
        <span className="text-xs text-text-dim font-mono">{r.tradingDate}</span>
        {r.archivedAt && (
          <>
            <span className="text-xs text-text-faint">·</span>
            <span className="text-[10px] text-text-faint" title={r.archivedAt}>
              wrapped {ago(r.archivedAt)}
            </span>
          </>
        )}
        <span className="ml-auto text-[10px] font-mono text-text-faint truncate max-w-[180px]" title={r.sessionId}>
          {r.sessionId}
        </span>
      </header>

      <div className="text-sm leading-relaxed text-text whitespace-pre-wrap font-sans">
        {highlight ? <Highlight text={body} query={highlight} /> : body}
      </div>

      {needsCollapse && (
        <button
          onClick={() => setExpanded((x) => !x)}
          className={cn(
            'mt-2 inline-flex items-center gap-1 text-xs text-text-dim hover:text-kam-violet',
          )}
        >
          {expanded
            ? <><ChevronDown size={12} /> Collapse</>
            : <><ChevronRight size={12} /> Show full</>}
        </button>
      )}
    </article>
  );
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${escapeRegex(query)})`, 'gi'));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="bg-kam-amber/30 text-text rounded px-0.5">{p}</mark>
          : <span key={i}>{p}</span>,
      )}
    </>
  );
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
